import { useEffect, useRef } from "react";
import { api } from "../lib/api";

const MOVING_MS = 2 * 60 * 1000;
const STILL_MS = 15 * 60 * 1000;
const MOVE_M = 50;
export const LOC_KEY = "nexus_loc";
export const LOC_ON_KEY = "nexus_loc_on";

const dist = (a, b) => {
  const R = 6371000, r = Math.PI / 180;
  const dLat = (b.lat - a.lat) * r, dLng = (b.lng - a.lng) * r;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * r) * Math.cos(b.lat * r) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export const isPhone = () => /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");

// Phone only, while Nexus is open: POST every 2 min when moving, every 15 when stationary.
export function useOwnerLocation() {
  const cur = useRef(null);
  const sent = useRef({ at: 0, pos: null });

  useEffect(() => {
    if (!isPhone() || !navigator.geolocation) return undefined;
    let watch = null, timer = null, alive = true;

    const post = async (force = false) => {
      const p = cur.current;
      if (!p || localStorage.getItem(LOC_ON_KEY) === "0") return;
      const since = Date.now() - sent.current.at;
      const moving = (p.speed || 0) > 0.7 || (sent.current.pos && dist(sent.current.pos, p) > MOVE_M);
      if (!force && since < (moving ? MOVING_MS : STILL_MS)) return;
      sent.current = { at: Date.now(), pos: p };
      try {
        const r = (await api.post("/nexus/location", p)).data;
        if (r.stored) localStorage.setItem(LOC_KEY, JSON.stringify({ ...p, address: r.address, ts: Date.now() }));
        if (r.enabled === false) localStorage.setItem(LOC_ON_KEY, "0");
      } catch (e) { /* retry next tick */ }
    };

    const start = () => {
      if (watch != null) return;
      watch = navigator.geolocation.watchPosition(
        (pos) => {
          cur.current = { lat: pos.coords.latitude, lng: pos.coords.longitude, acc: pos.coords.accuracy, speed: pos.coords.speed };
          post(sent.current.at === 0);
        },
        () => {},
        { enableHighAccuracy: true, maximumAge: 30000, timeout: 30000 }
      );
      timer = setInterval(() => post(false), 30000);
    };
    const stop = () => {
      if (watch != null) navigator.geolocation.clearWatch(watch);
      if (timer) clearInterval(timer);
      watch = null; timer = null;
    };
    const sync = () => (localStorage.getItem(LOC_ON_KEY) === "0" ? stop() : start());

    api.get("/nexus/location").then((r) => {
      if (!alive) return;
      localStorage.setItem(LOC_ON_KEY, r.data.enabled ? "1" : "0");
      sync();
    }).catch(() => {});
    window.addEventListener("nexus-loc-toggle", sync);
    return () => { alive = false; stop(); window.removeEventListener("nexus-loc-toggle", sync); };
  }, []);
}

// Header for chat requests: latest fix + address, only if tracking on and fresh (<30 min).
export const locationHeader = () => {
  try {
    if (localStorage.getItem(LOC_ON_KEY) === "0") return null;
    const l = JSON.parse(localStorage.getItem(LOC_KEY) || "null");
    if (!l || Date.now() - l.ts > 30 * 60 * 1000) return null;
    return JSON.stringify({ lat: l.lat, lng: l.lng, acc: l.acc, address: l.address || "" })
      .replace(/[\u007f-\uffff]/g, (c) => "\\u" + c.charCodeAt(0).toString(16).padStart(4, "0"));
  } catch (e) { return null; }
};
